import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { isOfflineSessionKind } from '@/constants/session-kind'
import { useStore } from '@/store/useStore'
import InternalMeetingRecordView from './components/InternalMeetingRecordView'
import { resolveRouteSessionKind } from './utils/resolveRouteSessionKind'

/**
 * Offline / internal meeting capture at `/meetings/:id/record` (BRAI-460).
 */
export default function InternalMeetingRecordPage() {
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const routeMeetingId = params.id ?? null

  const theme = useStore((s) => s.theme)
  const meetings = useStore((s) => s.meetings)
  const currentMeetingId = useStore((s) => s.currentMeetingId)
  const selectMeeting = useStore((s) => s.selectMeeting)
  const settingsSessionKind = useStore((s) => s.settings.meetingSetup.sessionKind)
  const pendingStartMeetingId = useStore((s) => s.settings.pendingStartMeetingId)

  const meetingConfirmedRef = useRef(false)

  useEffect(() => {
    meetingConfirmedRef.current = false
  }, [routeMeetingId])

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  useEffect(() => {
    if (!routeMeetingId) {
      navigate('/home', { replace: true })
      return
    }

    const meetingExists = meetings.some((meeting) => meeting.id === routeMeetingId)
    if (!meetingExists) {
      if (!meetingConfirmedRef.current) {
        navigate('/home', { replace: true })
      }

      // End Meeting removes the temporary meeting from the store immediately
      // before navigating to the persisted Meeting Minutes route. Do not let
      // this guard race that navigation with a fallback redirect.
      return
    }
    meetingConfirmedRef.current = true

    const routeSessionKind = resolveRouteSessionKind(routeMeetingId, {
      meetings,
      pendingStartMeetingId,
      settingsSessionKind,
    })
    if (!isOfflineSessionKind(routeSessionKind)) {
      navigate(`/meetings/${routeMeetingId}/live`, { replace: true })
      return
    }

    if (currentMeetingId !== routeMeetingId) {
      selectMeeting(routeMeetingId)
    }
  }, [
    currentMeetingId,
    meetings,
    navigate,
    pendingStartMeetingId,
    routeMeetingId,
    selectMeeting,
    settingsSessionKind,
  ])

  if (!routeMeetingId) return null

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-white text-slate-950 dark:bg-[var(--bg-secondary)] dark:text-[var(--text-primary)]">
      <InternalMeetingRecordView meetingId={routeMeetingId} />
    </div>
  )
}
